import { z } from "zod";
import { canonicalSha256 } from "./canonicalize.js";
import { protocolIdSchema, protocolTimestampSchema, protocolVersionSchema, sha256DigestSchema, UNIVERSAL_VERIFICATION_PROTOCOL, } from "./identifiers.js";
export const durableLoopJobSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    tenantId: protocolIdSchema,
    runId: protocolIdSchema,
    attemptId: protocolIdSchema,
    contractDigest: sha256DigestSchema,
    idempotencyKey: protocolIdSchema,
    state: z.enum(["QUEUED", "LEASED", "COMPLETED", "FAILED", "RESUMABLE"]),
    leaseOwnerId: protocolIdSchema.nullable(),
    leaseExpiresAt: protocolTimestampSchema.nullable(),
    heartbeatAt: protocolTimestampSchema.nullable(),
    priorReceiptDigests: z.array(sha256DigestSchema),
    effectDigest: sha256DigestSchema.nullable(),
    resumeToken: sha256DigestSchema,
    createdAt: protocolTimestampSchema,
    updatedAt: protocolTimestampSchema,
})
    .strict();
function transitionTime(job, now) {
    if (!protocolTimestampSchema.safeParse(now).success || !Number.isFinite(Date.parse(now))) {
        throw new Error("durable loop job transition requires a valid timestamp");
    }
    const time = Date.parse(now);
    if (time < Date.parse(job.updatedAt) || time < Date.parse(job.createdAt)) {
        throw new Error("durable loop job transition requires monotonic time");
    }
    return time;
}
function assertLeaseAcquisition(job, input) {
    const now = transitionTime(job, input.now);
    if (!protocolTimestampSchema.safeParse(input.leaseExpiresAt).success ||
        !Number.isFinite(Date.parse(input.leaseExpiresAt)) ||
        Date.parse(input.leaseExpiresAt) <= now) {
        throw new Error("durable loop job lease requires a future expiry");
    }
    if ((job.state === "LEASED" || job.state === "RESUMABLE") &&
        job.leaseExpiresAt !== null &&
        Date.parse(job.leaseExpiresAt) > now &&
        job.leaseOwnerId !== input.workerId) {
        throw new Error("durable loop job cannot replace another worker's active lease");
    }
}
function assertActiveLease(job, input, action) {
    const now = transitionTime(job, input.now);
    if (job.leaseOwnerId !== input.workerId) {
        throw new Error(`durable loop job ${action} requires current lease owner`);
    }
    if ((job.state !== "LEASED" && job.state !== "RESUMABLE") ||
        job.leaseExpiresAt === null ||
        Date.parse(job.leaseExpiresAt) <= now) {
        throw new Error(`durable loop job ${action} requires an unexpired lease`);
    }
}
export function createDurableLoopJob(input) {
    const resumeToken = canonicalSha256({
        id: input.id,
        tenantId: input.tenantId,
        runId: input.runId,
        attemptId: input.attemptId,
        contractDigest: input.contractDigest,
        idempotencyKey: input.idempotencyKey,
    }, input.sha256);
    return durableLoopJobSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        tenantId: input.tenantId,
        runId: input.runId,
        attemptId: input.attemptId,
        contractDigest: input.contractDigest,
        idempotencyKey: input.idempotencyKey,
        state: "QUEUED",
        leaseOwnerId: null,
        leaseExpiresAt: null,
        heartbeatAt: null,
        priorReceiptDigests: [...(input.priorReceiptDigests ?? [])],
        effectDigest: null,
        resumeToken,
        createdAt: input.now,
        updatedAt: input.now,
    });
}
export function leaseDurableLoopJob(input) {
    const job = durableLoopJobSchema.parse(input.job);
    if (job.state === "COMPLETED")
        return job;
    assertLeaseAcquisition(job, input);
    return durableLoopJobSchema.parse({
        ...job,
        state: "LEASED",
        leaseOwnerId: input.workerId,
        leaseExpiresAt: input.leaseExpiresAt,
        heartbeatAt: input.now,
        updatedAt: input.now,
    });
}
export function heartbeatDurableLoopJob(input) {
    const job = durableLoopJobSchema.parse(input.job);
    assertActiveLease(job, input, "heartbeat");
    return durableLoopJobSchema.parse({
        ...job,
        heartbeatAt: input.now,
        updatedAt: input.now,
    });
}
export function resumeDurableLoopJob(input) {
    const job = durableLoopJobSchema.parse(input.job);
    if (job.state === "COMPLETED")
        return job;
    assertLeaseAcquisition(job, input);
    return durableLoopJobSchema.parse({
        ...job,
        state: "RESUMABLE",
        leaseOwnerId: input.workerId,
        leaseExpiresAt: input.leaseExpiresAt,
        heartbeatAt: input.now,
        updatedAt: input.now,
    });
}
export function completeDurableLoopJob(input) {
    const job = durableLoopJobSchema.parse(input.job);
    if (input.existingCompletedJob?.state === "COMPLETED") {
        const existing = durableLoopJobSchema.parse(input.existingCompletedJob);
        const identityFields = [
            "tenantId",
            "id",
            "runId",
            "attemptId",
            "contractDigest",
            "idempotencyKey",
            "resumeToken",
        ];
        if (identityFields.some((field) => existing[field] !== job[field])) {
            throw new Error("durable loop job completion identity mismatch");
        }
        return existing;
    }
    // A queue replay is a read of the first result, never a second effect or
    // receipt append. Persistent callers must still fence reads and writes.
    if (job.state === "COMPLETED")
        return job;
    assertActiveLease(job, input, "completion");
    return durableLoopJobSchema.parse({
        ...job,
        state: "COMPLETED",
        leaseOwnerId: input.workerId,
        priorReceiptDigests: [...job.priorReceiptDigests, input.receiptDigest],
        effectDigest: canonicalSha256({
            idempotencyKey: job.idempotencyKey,
            receiptDigest: input.receiptDigest,
            effectInput: input.effectInput,
        }, input.sha256),
        updatedAt: input.now,
    });
}
